import type {
  ConditionOperator,
  CountOperator,
  ForeignKeyOn,
  RelativeDate,
  BetweenDate,
} from '@reach/shared-types/conditions';

/**
 * Field type used for operator filtering in the visual builder.
 * Matches FieldInfo.type from segment-definitions-api.
 */
export type BuilderFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'array'
  | 'reference'
  | 'unknown';

/** Value for a field condition (matches condition value array elements). */
export type BuilderConditionValue = string | number | boolean | Date | RelativeDate | BetweenDate;

/** Builder representation of a field condition. */
export interface BuilderFieldCondition {
  id: string;
  type: 'field';
  field: string;
  fieldType: BuilderFieldType;
  operator: ConditionOperator;
  value: BuilderConditionValue[];
  description?: string;
}

/** Builder representation of a related resource condition. */
export interface BuilderRelatedResourceCondition {
  id: string;
  type: 'related_resource';
  schemaId: string;
  schemaName: string;
  foreignKeyPath: string;
  /**
   * Which side holds the FK. Preserved exactly as stored (absent on segments
   * that predate the field) so untouched round-trips stay byte-identical.
   */
  foreignKeyOn?: ForeignKeyOn;
  unavailableReason?: string;
  countOperator: CountOperator;
  countValue: number;
  /**
   * Field conditions on the related record, plus nested related-resource
   * conditions walking one more relationship hop (capped by
   * MAX_RELATED_RESOURCE_DEPTH).
   */
  conditions: (BuilderFieldCondition | BuilderRelatedResourceCondition)[];
  isExpanded: boolean;
  description?: string;
}

/**
 * Builder representation of a nested condition group.
 * Allows arbitrary nesting of AND/OR logic within a parent group.
 */
export interface BuilderNestedGroup {
  id: string;
  type: 'nested_group';
  logic: 'and' | 'or';
  conditions: BuilderCondition[];
  isExpanded: boolean;
  description?: string;
}

/** Union of builder condition types (recursive via BuilderNestedGroup). */
export type BuilderCondition =
  | BuilderFieldCondition
  | BuilderRelatedResourceCondition
  | BuilderNestedGroup;

/** Single condition group in the builder (top-level). */
export interface BuilderGroup {
  id: string;
  logic: 'and' | 'or';
  conditions: BuilderCondition[];
  description?: string;
}

/** Full builder state (array of groups). */
export interface BuilderState {
  groups: BuilderGroup[];
}

export function isBuilderFieldCondition(c: BuilderCondition): c is BuilderFieldCondition {
  return c.type === 'field';
}

export function isBuilderRelatedResourceCondition(
  c: BuilderCondition,
): c is BuilderRelatedResourceCondition {
  return c.type === 'related_resource';
}

export function isBuilderNestedGroup(c: BuilderCondition): c is BuilderNestedGroup {
  return c.type === 'nested_group';
}
